// @flow
import * as React from 'react';
import {HeroSection, Heading, List, ListItem} from 'docs/components';
import {ButtonLink} from 'sail/Button';
import Box from 'sail/Box';
import Icon from 'sail/Icon';
import {UnstyledLink} from 'sail/UnstyledLink';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import type {IconType} from 'sail/Icon/types';
import {Subheading, Text} from 'sail/Text';
import './HomePage.scss';

import PaymentHeroImage from 'docs/images/home/payment-hero.svg';
import BusinessOperationsImage from 'docs/images/home/business-operations.svg';
import FinancialServicesImage from 'docs/images/home/financial-services.svg';

const messages = defineMessages({
  inThisSection: {
    id: 'docsShell.HomePage.SplitHero.inThisSection',
    description:
      'This text is a sub heading in a guide card that describes words inside of a section',
    defaultMessage: 'In this section',
  },
  getStarted: {
    id: 'docsShell.HomePage.Hero.getStarted',
    description:
      'Text for a button explaining how to get started with payments',
    defaultMessage: 'Get started',
  },
  heroSectionDescription: {
    id: 'docsShell.HomePage.Hero.description',
    description: 'How to build an integration',
    defaultMessage:
      'Build a web or mobile integration to accept payments online or in person.',
  },
  businessOperationsTitle: {
    id: 'docsShell.HomePage.SplitHero.BusinessOperations.title',
    description: 'Business operations title',
    defaultMessage: 'Business operations',
  },
  businessOperationsSubtitle: {
    id: 'docsShell.HomePage.SplitHero.BusinessOperations.subtitle',
    description: 'This is a subtitle for the heading Business Operations',
    defaultMessage:
      'Programmatically or manually monitor, protect, and report on the money you make with Stripe.',
  },
  financialServicesTitle: {
    id: 'docsShell.HomePage.SplitHero.FinancialServices.title',
    description: 'Financial Services title',
    defaultMessage: 'Financial services',
  },
  financialServicesSubtitle: {
    id: 'docsShell.HomePage.SplitHero.FinancialServices.subtitle',
    description: 'Subtitle for financial services section',
    defaultMessage:
      "Move, control, and borrow money with Stripe's APIs and financial services.",
  },
  useCaseDeveloperToolsHeading: {
    id: 'docsShell.HomePage.UseCase.DeveloperTools.heading',
    description: 'Heading for a section under Use Cases',
    defaultMessage: 'Developer tools',
  },
  useCaseDeveloperToolsBody: {
    id: 'docsShell.HomePage.UseCase.DeveloperTools.body',
    description:
      'Body text under a section under Use Cases for developer tools',
    defaultMessage:
      'Get up and running with libraries, keys, and integration tools.',
  },
  useCaseSecurityHeading: {
    id: 'docsShell.HomePage.UseCase.Security.heading',
    description: 'Heading for a section under Use Cases',
    defaultMessage: 'Security',
  },
  useCaseSecurityBody: {
    id: 'docsShell.HomePage.UseCase.Security.body',
    description: 'Body text under a section under Use Cases for security',
    defaultMessage:
      'Strengthen your integration security and learn about sensitive data.',
  },
  useCaseAccountHeading: {
    id: 'docsShell.HomePage.UseCase.Account.heading',
    description: 'Heading for a section under Use Cases',
    defaultMessage: 'Account',
  },
  useCaseAccountBody: {
    id: 'docsShell.HomePage.UseCase.Account.body',
    description: 'Body text under a section under Use Cases for account',
    defaultMessage: 'Activate, manage, and configure your account.',
  },
  quicklinksOnline: {
    id: 'docsShell.HomePage.QuickLink.online',
    description: 'Link to Online payments',
    defaultMessage: 'Online',
  },
  quickLinksInPerson: {
    id: 'docsShell.HomePage.QuickLink.inperson',
    description: 'Link to in person payments',
    defaultMessage: 'In-person',
  },
  quicklinksSubscriptions: {
    id: 'docsShell.HomePage.QuickLink.subscriptions',
    description: 'Link to subscriptions',
    defaultMessage: 'Subscriptions',
  },
  quicklinksMarketplaces: {
    id: 'docsShell.HomePage.QuickLink.marketplaces',
    description: 'Link to marketplaces',
    defaultMessage: 'Marketplaces',
  },
  businessOperationsSectionDisputes: {
    id: 'docsShell.HomePage.SplitHero.BusinessOperations.section.disputes',
    description: 'Link to documentation about preventing and handling disputes',
    defaultMessage: 'Prevent and handle disputes',
  },
  businessOperationsSectionReports: {
    id: 'docsShell.HomePage.SplitHero.BusinessOperations.section.reports',
    description: 'Link to documentation about how to generate reports',
    defaultMessage: 'Generate reports',
  },
  financialServicesSectionIssueCards: {
    id: 'docsShell.HomePage.SplitHero.FinanicalServices.section.issueCards',
    description: 'Issue cards instantly',
    defaultMessage: 'Issue cards instantly',
  },
  financialServicesSectionCorporateCard: {
    id: 'docsShell.HomePage.SplitHero.FinanicalServices.section.corpCard',
    description: 'Learn about the Stripe product the Stripe Corporate Card',
    defaultMessage: 'Learn about the Stripe Corporate Card',
  },
  exploreUsingStripe: {
    id: 'docsShell.HomePage.UseCase.exploreUsingStripe',
    description:
      'Header for section about other Stripe things on https://stripe.com/docs',
    defaultMessage: 'Explore using Stripe',
  },
});

type SplitHeroProps = {|
  title: string,
  subtitle: string,
  learnMoreLink: string,
  sections: Array<{|text: string, href: string|}>,
  imageSrc: string,
|};
const SplitHero = ({
  title,
  subtitle,
  learnMoreLink,
  sections,
  imageSrc,
}: SplitHeroProps) => {
  return (
    <Box className="HomePage-SplitHero">
      <a className="HomePage-SplitHero-image-container" href={learnMoreLink}>
        {/* This image is decorative, hence the empty alt text */}
        <img alt="" src={imageSrc} />
      </a>
      <Heading level={2}>{title}</Heading>
      <Box margin={{top: 4, bottom: 12}}>
        <Text lineHeight={24} size={16}>
          {subtitle}
        </Text>
      </Box>
      <ButtonLink
        icon={'arrowRight'}
        iconPosition="right"
        label={'Learn more'}
        size="large"
        href={learnMoreLink}
      />
      <Box margin={{top: 12}}>
        <Subheading color="gray400">
          <FormattedMessage {...messages.inThisSection} />
        </Subheading>
        <List type="guide">
          {sections.map(({text, href}) => {
            return (
              <ListItem key={href}>
                <a href={href}>{text}</a>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

type UseCaseProps = {|
  icon: IconType,
  heading: string,
  body: string,
  href: string,
|};

const UseCase = ({icon, heading, body, href}: UseCaseProps) => (
  <UnstyledLink href={href} className="HomePage-UseCase">
    <Icon icon={icon} size={32} color="blue" />
    <Box margin={{top: 16}}>
      <Text
        color="blue"
        weight="bold"
        size={16}
        className="HomePage-UseCase--header"
      >
        {heading}
      </Text>
    </Box>
    <Box margin={{top: 4}}>
      <Text lineHeight={24} size={16}>
        {body}
      </Text>
    </Box>
  </UnstyledLink>
);

function EqualWidthColumns({children, spacing}) {
  return (
    <Box flex={{direction: 'row'}} margin={{top: 32, bottom: 64}}>
      {React.Children.map(children, (child, i) => (
        <Box
          flexBasis={0}
          flexGrow={1}
          flexShrink={1}
          margin={i !== 0 ? {left: spacing} : undefined}
        >
          {child}
        </Box>
      ))}
    </Box>
  );
}

export const HomePage = () => {
  const intl = useIntl();

  return (
    <Box className="HomePage">
      <HeroSection
        title="Payments"
        imageSrc={PaymentHeroImage}
        linkLabel={intl.formatMessage(messages.getStarted)}
        linkHref="/docs/payments?payments=popular"
        useButton
        quickLinks={[
          {
            label: intl.formatMessage(messages.quicklinksOnline),
            href: '/docs/payments?payments=online',
          },
          {
            label: intl.formatMessage(messages.quickLinksInPerson),
            href: '/docs/payments?payments=in-person',
          },
          {
            label: intl.formatMessage(messages.quicklinksSubscriptions),
            href: '/docs/payments?payments=subscriptions',
          },
          {
            label: intl.formatMessage(messages.quicklinksMarketplaces),
            href: '/docs/payments#multiparty-payments',
          },
        ]}
      >
        {/* This has to be in a paragraph to match the styles with the Markdoc hero section tag */}
        <p>
          <FormattedMessage {...messages.heroSectionDescription} />
        </p>
      </HeroSection>
      <Box
        flex={{direction: 'row', justifyContent: 'space-between'}}
        margin={{top: 48, bottom: 32}}
        className="HomePage-SplitHero-container"
      >
        <SplitHero
          title={intl.formatMessage(messages.businessOperationsTitle)}
          subtitle={intl.formatMessage(messages.businessOperationsSubtitle)}
          learnMoreLink="/docs/business"
          imageSrc={BusinessOperationsImage}
          sections={[
            {
              text: intl.formatMessage(
                messages.businessOperationsSectionDisputes,
              ),
              href: '/docs/disputes',
            },
            {
              text: intl.formatMessage(
                messages.businessOperationsSectionReports,
              ),
              href: '/docs/reports',
            },
          ]}
        />
        <div className="HomePage-divider" />
        <SplitHero
          title={intl.formatMessage(messages.financialServicesTitle)}
          subtitle={intl.formatMessage(messages.financialServicesSubtitle)}
          learnMoreLink="/docs/financial-services"
          imageSrc={FinancialServicesImage}
          sections={[
            {
              text: intl.formatMessage(
                messages.financialServicesSectionIssueCards,
              ),
              href: '/docs/issuing',
            },
            {
              text: intl.formatMessage(
                messages.financialServicesSectionCorporateCard,
              ),
              href: '/docs/corporate-card',
            },
          ]}
        />
      </Box>
      <Box className="HomePage-ExploreUsingStripe">
        <Heading level={2}>
          <FormattedMessage {...messages.exploreUsingStripe} />
        </Heading>
        <EqualWidthColumns spacing={64}>
          <UseCase
            icon="api"
            href="/docs/development"
            heading={intl.formatMessage(messages.useCaseDeveloperToolsHeading)}
            body={intl.formatMessage(messages.useCaseDeveloperToolsBody)}
          />
          <UseCase
            icon="lock"
            href="/docs/security"
            heading={intl.formatMessage(messages.useCaseSecurityHeading)}
            body={intl.formatMessage(messages.useCaseSecurityBody)}
          />
          <UseCase
            icon="person"
            href="/docs/account"
            heading={intl.formatMessage(messages.useCaseAccountHeading)}
            body={intl.formatMessage(messages.useCaseAccountBody)}
          />
        </EqualWidthColumns>
      </Box>
    </Box>
  );
};
